import React, { useState } from 'react';
import { TrackerSuggestion } from '../../types';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import TrackerModal from './TrackerModal';
import { fmtLong, host } from './helpers';

interface Props {
  suggestions: TrackerSuggestion[];
  canReview: boolean;
  onClose: () => void;
  onReviewed: () => void;
}

const STATUS_LABEL: Record<string, string> = { pending: 'Pending verification', verified: 'Verified · added', rejected: 'Not confirmed' };

const SuggestionsQueueModal: React.FC<Props> = ({ suggestions, canReview, onClose, onReviewed }) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sorted = suggestions.slice().sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));

  const act = async (s: TrackerSuggestion, decision: 'verified' | 'rejected') => {
    setError(null);
    const note = decision === 'rejected' ? (window.prompt('Reason for rejecting (optional)') || '') : '';
    setBusyId(s.id);
    try {
      await catalystTrackerApi.reviewSuggestion(s.id, decision, note);
      onReviewed();
    } catch (e: any) {
      setError(e?.response?.data?.error === 'already_reviewed' ? 'Already reviewed by someone else.' : 'Could not record the review.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <TrackerModal title="Suggestions" onClose={onClose}>
      <p className="policy">
        Suggestions don&rsquo;t change the calendar. Each one is checked against an independent public source and
        added only if it&rsquo;s confirmed.
      </p>
      {error && <p className="note">{error}</p>}
      {!sorted.length && (
        <div className="empty" style={{ margin: 0 }}>
          <b>No suggestions yet</b>
          Use &ldquo;Suggest&rdquo; to propose a catalyst or correction.
        </div>
      )}
      {sorted.map((s) => {
        const status = s.status_review || 'pending';
        return (
          <div className="qitem" key={s.id}>
            <div className="top">
              <span className={`qs ${status}`}>{STATUS_LABEL[status] || status}</span>
              <span>{s.kind === 'correction' ? 'Correction' : 'New catalyst'}</span>
              {s.submittedAt && <span>{fmtLong(s.submittedAt)}</span>}
            </div>
            <div className="t">{s.company}{s.asset ? ` · ${s.asset}` : ''}{s.trial ? ` · ${s.trial}` : ''}</div>
            {s.event && <div>{s.event}</div>}
            {s.reason && <div style={{ color: 'var(--muted)', fontSize: 13 }}>{s.reason}</div>}
            {s.review_note && <div style={{ fontSize: 13 }}><b>Review:</b> {s.review_note}</div>}
            {s.source && <a href={s.source} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5 }}>{host(s.source)}</a>}
            {canReview && status === 'pending' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button type="button" className="ib" disabled={busyId === s.id} onClick={() => act(s, 'verified')}>Verify</button>
                <button type="button" className="ib" disabled={busyId === s.id} onClick={() => act(s, 'rejected')}>Reject</button>
              </div>
            )}
          </div>
        );
      })}
    </TrackerModal>
  );
};

export default SuggestionsQueueModal;
